import React, { useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { images } from '../data/mock';

const QuickCheckPage = () => {
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const ctaLink = "https://calendly.com/davwaysolution/review";

  const questions = [
    {
      id: 1,
      label: "1. Monthly card volume?",
      options: ["Under $10K", "$10K–$50K", "$50K–$150K", "$150K+"]
    },
    {
      id: 2,
      label: "2. What system do you use?",
      options: ["Square", "GlossGenius", "Mindbody", "Clover", "Other / Not sure"]
    },
    {
      id: 3,
      label: "3. Do you know your effective processing rate?",
      options: ["Yes", "Not sure", "No"]
    },
    {
      id: 4,
      label: "4. When did you last review your statement?",
      options: ["Last 30 days", "3–6 months", "6+ months", "Never"]
    },
    {
      id: 5,
      label: "5. Are you actively reducing or managing fees?",
      options: ["Yes", "Somewhat", "No"]
    }
  ];

  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / questions.length) * 100;

  const handleSelect = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (answeredCount < questions.length) return;
    setShowResult(true);
    setTimeout(() => {
      document.getElementById('quiz-result')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: '#ffffff' }}>
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#111827' }}>Quick Payment Check</h1>
          <p className="text-gray-500">Answer a few quick questions to see if your setup may be costing more than it should.</p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <p className="text-sm text-gray-500 mb-2">Step {answeredCount} of {questions.length}</p>
          <div className="w-full h-2 rounded-full" style={{ backgroundColor: '#e5e7eb' }}>
            <div 
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${progress}%`, backgroundColor: '#2563eb' }}
            ></div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {questions.map((q) => (
            <div key={q.id} className="mb-5 pb-5 border-b border-gray-200">
              <label className="block font-medium text-sm mb-2" style={{ color: '#111827' }}>{q.label}</label>
              <select
                className="w-full px-3 py-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:border-blue-500 transition-colors"
                value={answers[q.id] || ''}
                onChange={(e) => handleSelect(q.id, e.target.value)}
                required
                data-testid={`quiz-q${q.id}`}
              >
                <option value="">Select</option>
                {q.options.map((opt, i) => (
                  <option key={i} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          ))}

          <Button
            type="submit"
            className="w-full text-white font-bold text-base py-5 rounded-lg"
            style={{ backgroundColor: '#2563eb' }}
            disabled={answeredCount < questions.length}
            data-testid="quiz-submit-btn"
          >
            See My Results
          </Button>
        </form>

        {/* Result */}
        {showResult && (
          <div id="quiz-result" className="mt-8 p-6 rounded-xl border border-gray-200 text-center" style={{ backgroundColor: '#f9fafb' }}>
            <img 
              src={images.logo}
              alt="Davway Solutions"
              className="mx-auto mb-4"
              style={{ height: '60px' }}
            />
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(37, 99, 235, 0.1)' }}>
              <CheckCircle style={{ color: '#2563eb' }} size={28} />
            </div>
            <h2 className="text-xl font-bold mb-3" style={{ color: '#111827' }}>
              There's a strong chance your setup isn't fully optimized.
            </h2>
            <p className="text-gray-500 mb-6">
              Most businesses don't realize what they're actually paying until it's broken down clearly.
            </p>
            <a href={ctaLink} target="_blank" rel="noopener noreferrer">
              <Button
                className="text-white font-bold px-8 py-4 rounded-lg hover:scale-105 transition-all group"
                style={{ backgroundColor: '#2563eb' }}
                data-testid="quiz-cta-btn"
              >
                Get a Free 15-Min Fee Breakdown
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
              </Button>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickCheckPage;
